import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, ShieldCheck, Home, Wrench, Briefcase, ArrowRight } from 'lucide-react';
import { getServices } from '../lib/cms';

const DynamicIcon = ({ name, className }) => {
  const icons = {
    Hammer: Hammer,
    ShieldCheck: ShieldCheck,
    Home: Home,
    Wrench: Wrench,
    Briefcase: Briefcase,
  };
  const IconComponent = icons[name] || Hammer;
  return <IconComponent className={className} />;
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Vores Ydelser | Tømrer-, Tag- & Entreprenørarbejde på Sjælland | Toa Byg ApS";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Se alle ydelser hos Toa Byg ApS. Vi tilbyder professionelt tagarbejde, nedtagning af asbesttag, tømrerarbejde, renovering og totalentreprise i Ishøj og på Sjælland.');

    async function loadServices() {
      try {
        const data = await getServices();
        setServices(data);
      } catch (err) {
        console.error('Fejl ved indlæsning af ydelser:', err);
      } finally {
        setLoading(false);
      }
    }
    loadServices();
  }, []);

  return (
    <div className="bg-slate-50">
      {/* Header Banner */}
      <section className="bg-blue-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">Vores Ydelser</h1>
          <p className="mt-4 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Professionelt tømrerarbejde, tagløsninger og renovering udført med stolthed og fokus på kvalitet.
          </p>
        </div>
      </section>

      {/* Ydelser Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-none overflow-hidden shadow-md hover:shadow-xl transition-all flex flex-col h-full group border border-slate-100"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image?.[0]}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-8 flex flex-col flex-grow">
                    <h2 className="text-2xl font-bold text-slate-900 mb-3 uppercase">{service.title}</h2>
                    <p className="text-slate-600 text-sm mb-6 flex-grow leading-relaxed">
                      {service.description}
                    </p>
                    <Link
                      to={`/ydelser/${service.slug?._ || service.id}`}
                      className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors mt-auto group/link uppercase tracking-wider"
                    >
                      Læs mere om {service.title}
                      <ArrowRight className="ml-1.5 h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Kvalitet & Tryghed Sektion */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex p-4 bg-blue-800/50 rounded-none mb-6 border border-blue-700/50">
            <ShieldCheck className="h-10 w-10 text-blue-300" />
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight uppercase">Din tryghed er vores prioritet</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Hos Toa Byg ApS leverer vi solidt håndværk, der lever op til alle gældende standarder og lovkrav. Vi sikrer en tæt dialog gennem hele forløbet, så du altid er tryg ved byggeriets kvalitet og tidsplan.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-none bg-blue-950/40 text-sm font-semibold border border-blue-800/40 uppercase">
              ✓ Erfarne tømrere
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-none bg-blue-950/40 text-sm font-semibold border border-blue-800/40 uppercase">
              ✓ Solidt materialevalg
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-none bg-blue-950/40 text-sm font-semibold border border-blue-800/40 uppercase">
              ✓ Overholdte aftaler
            </span>
          </div>
        </div>
      </section>

      {/* Kontakt CTA */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
            Har du et specifikt projekt i tankerne?
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Vi rådgiver dig gerne om materialevalg, konstruktioner og priser. Kontakt os i dag for en uforpligtende snak og et uforpligtende tilbud.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md uppercase tracking-wider"
            >
              Kontakt os nu
            </Link>
            <a
              href="tel:+4540942420"
              className="inline-flex items-center justify-center px-6 py-3 border border-slate-300 text-base font-semibold rounded-none text-slate-700 bg-white hover:bg-slate-50 transition-colors uppercase tracking-wider"
            >
              Ring +45 40 94 24 20
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
